import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
    Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine, Cell,
} from 'recharts';
import { Loader2, TrendingUp, Trophy, Target } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const FE_ORDER = ['Génipa', 'Cluny', 'Dillon', 'Océanis', 'Vauclin'];

/** Returns Monday-of-current-week and Sunday-of-current-week as YYYY-MM-DD. */
function thisMonthRange() {
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    return { start: fmt(start), end: fmt(end) };
}

/**
 * Top produits / Pareto.
 * Lists the products ranked by CA or Qté over a period with their share %, cumulative %
 * and a Pareto combo chart (bars = share, line = cumulative). 80% reference line included.
 */
export default function TopProductsTab({ sites = [] }) {
    const init = thisMonthRange();
    const [startDate, setStartDate] = useState(init.start);
    const [endDate, setEndDate] = useState(init.end);
    const [metric, setMetric] = useState('ca'); // 'ca' | 'qty'
    const [siteFilter, setSiteFilter] = useState('all'); // 'all' or site_id
    const [limit, setLimit] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const feSites = useMemo(() => {
        const list = (sites || []).filter(s => FE_ORDER.some(n => s.name?.endsWith(n)));
        // Canonical order
        list.sort((a, b) => {
            const ai = FE_ORDER.findIndex(n => a.name?.endsWith(n));
            const bi = FE_ORDER.findIndex(n => b.name?.endsWith(n));
            return ai - bi;
        });
        return list;
    }, [sites]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    start_date: startDate,
                    end_date: endDate,
                    metric,
                    limit: String(limit),
                });
                if (siteFilter !== 'all') params.set('site_ids', siteFilter);
                const r = await api.get(`${API}/api/sales/top-products?${params.toString()}`);
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) { toast.error("Erreur de chargement"); setData(null); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [startDate, endDate, metric, siteFilter, limit]);

    const setPreset = (preset) => {
        const today = new Date();
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (preset === 'week') {
            const monday = new Date(today);
            const dow = (monday.getDay() + 6) % 7;
            monday.setDate(today.getDate() - dow);
            const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
            setStartDate(fmt(monday)); setEndDate(fmt(sunday));
        } else if (preset === 'month') {
            const r = thisMonthRange(); setStartDate(r.start); setEndDate(r.end);
        } else if (preset === 'ytd') {
            setStartDate(`${today.getFullYear()}-01-01`); setEndDate(fmt(today));
        } else if (preset === 'last_30') {
            const s = new Date(today); s.setDate(today.getDate() - 29);
            setStartDate(fmt(s)); setEndDate(fmt(today));
        }
    };

    const products = data?.products || [];
    const totalLabel = metric === 'ca' ? formatCurrency(data?.total_ca || 0) : `${data?.total_qty || 0}`;
    const metricLabel = metric === 'ca' ? 'CA' : 'Quantité';
    const metricUnit = metric === 'ca' ? '€' : 'u.';

    // Bar colors: green for first 80% bucket (Pareto), amber for the rest
    const chartData = products.map((p, i) => ({
        ...p,
        short_name: (p.product_name || '').length > 22 ? p.product_name.slice(0, 22) + '…' : p.product_name,
        in_pareto: data?.pareto_80_count ? (i < data.pareto_80_count) : false,
    }));

    return (
        <div className="space-y-3" data-testid="top-products-tab">
            {/* Controls */}
            <Card>
                <CardContent className="pt-3 pb-3">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex items-center gap-1">
                            {[['week', 'Semaine'], ['month', 'Mois'], ['last_30', '30 derniers j.'], ['ytd', 'Année']].map(([k, lbl]) => (
                                <Button
                                    key={k}
                                    size="sm" variant="outline"
                                    onClick={() => setPreset(k)}
                                    className="h-7 text-xs"
                                    data-testid={`preset-${k}`}
                                >{lbl}</Button>
                            ))}
                        </div>
                        <div className="flex items-end gap-1">
                            <div>
                                <Label className="text-xs text-slate-500">Du</Label>
                                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-7 text-xs w-[135px]" data-testid="top-start-date" />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">au</Label>
                                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-7 text-xs w-[135px]" data-testid="top-end-date" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-slate-500">Métrique</Label>
                            <div className="flex border rounded overflow-hidden h-7" data-testid="top-metric-toggle">
                                <button
                                    type="button"
                                    onClick={() => setMetric('ca')}
                                    className={`px-3 text-xs font-medium ${metric === 'ca' ? 'bg-blue-500 text-white' : 'bg-white text-slate-600'}`}
                                    data-testid="top-metric-ca"
                                >CA €</button>
                                <button
                                    type="button"
                                    onClick={() => setMetric('qty')}
                                    className={`px-3 text-xs font-medium ${metric === 'qty' ? 'bg-blue-500 text-white' : 'bg-white text-slate-600'}`}
                                    data-testid="top-metric-qty"
                                >Quantité</button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-slate-500">Top N</Label>
                            <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v))}>
                                <SelectTrigger className="h-7 text-xs w-[90px]" data-testid="top-limit"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[10, 20, 30, 50, 100, 200].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                    </div>
                    {/* Row 2: site shortcuts (Fuji Express) */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="text-[11px] font-semibold text-slate-500 mr-1">Sites :</span>
                        <button
                            type="button"
                            onClick={() => setSiteFilter('all')}
                            className={`px-2.5 h-6 rounded-full text-[11px] font-semibold border ${siteFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                            data-testid="top-site-all"
                        >Tous FE</button>
                        {feSites.map(s => {
                            const short = (s.name || '').replace('FE ', '');
                            const active = siteFilter === s.id;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setSiteFilter(s.id)}
                                    className={`px-2.5 h-6 rounded-full text-[11px] font-semibold border ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                                    data-testid={`top-site-${short.toLowerCase()}`}
                                >{short}</button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* KPIs */}
            {data && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <KpiCard icon={<TrendingUp className="w-4 h-4" />} label={`${metricLabel} total`} value={totalLabel} accent="blue" />
                    <KpiCard icon={<Trophy className="w-4 h-4" />} label="Produits vendus" value={`${data.products_count}`} accent="slate" />
                    <KpiCard
                        icon={<Target className="w-4 h-4" />}
                        label={`Top 80% du ${metricLabel}`}
                        value={`${data.pareto_80_count} produits`}
                        sub={`(${data.pareto_80_share_of_catalog_pct}% du catalogue)`}
                        accent="emerald"
                    />
                    <KpiCard
                        icon={<Trophy className="w-4 h-4" />}
                        label="Produit #1"
                        value={products[0]?.product_name?.slice(0, 28) || '—'}
                        sub={products[0] ? `${metric === 'ca' ? formatCurrency(products[0].ca) : products[0].qty + ' u.'} — ${products[0].share_pct}%` : ''}
                        accent="amber"
                    />
                </div>
            )}

            {/* Pareto chart */}
            {chartData.length > 0 && (
                <Card>
                    <CardHeader className="pb-1">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Target className="w-4 h-4 text-emerald-500" />
                            Pareto — Distribution du {metricLabel} par produit
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="w-full" style={{ height: 360 }}>
                            <ResponsiveContainer>
                                <ComposedChart data={chartData} margin={{ top: 10, right: 25, left: 5, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="short_name"
                                        tick={{ fontSize: 10, fill: '#64748b' }}
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={70}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        tick={{ fontSize: 10, fill: '#64748b' }}
                                        tickFormatter={(v) => metric === 'ca' ? `${(v / 1000).toFixed(0)}k` : `${v}`}
                                    >
                                    </YAxis>
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        domain={[0, 100]}
                                        tick={{ fontSize: 10, fill: '#64748b' }}
                                        tickFormatter={(v) => `${v}%`}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => {
                                            if (name === 'Cumul %') return [`${(value || 0).toFixed(1)}%`, name];
                                            if (name === metricLabel) return [
                                                metric === 'ca' ? formatCurrency(value || 0) : `${value || 0} ${metricUnit}`,
                                                name,
                                            ];
                                            return [value, name];
                                        }}
                                        labelFormatter={(label, payload) => payload?.[0]?.payload?.product_name || label}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <ReferenceLine yAxisId="right" y={80} stroke="#10b981" strokeDasharray="5 5" label={{ value: 'Seuil 80%', position: 'right', fill: '#10b981', fontSize: 10 }} />
                                    <Bar
                                        yAxisId="left"
                                        dataKey={metric}
                                        name={metricLabel}
                                        radius={[3, 3, 0, 0]}
                                    >
                                        {chartData.map((entry, idx) => (
                                            <Cell key={idx} fill={entry.in_pareto ? '#10b981' : '#cbd5e1'} />
                                        ))}
                                    </Bar>
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="cumulative_pct"
                                        name="Cumul %"
                                        stroke="#f59e0b"
                                        strokeWidth={2}
                                        dot={{ r: 2, fill: '#f59e0b' }}
                                        activeDot={{ r: 4 }}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Ranking table */}
            {products.length > 0 && (
                <Card>
                    <CardHeader className="pb-1">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-amber-500" />
                            Classement des produits
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="top-products-table">
                                <thead className="bg-slate-50 border-b text-slate-500 uppercase text-[10px]">
                                    <tr>
                                        <th className="px-2 py-1.5 text-left">#</th>
                                        <th className="px-2 py-1.5 text-left">Produit</th>
                                        <th className="px-2 py-1.5 text-left">Code</th>
                                        <th className="px-2 py-1.5 text-right">CA</th>
                                        <th className="px-2 py-1.5 text-right">Qté</th>
                                        <th className="px-2 py-1.5 text-right">Part</th>
                                        <th className="px-2 py-1.5 text-left">Cumul</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p, i) => {
                                        const inPareto = data?.pareto_80_count ? i < data.pareto_80_count : false;
                                        return (
                                            <tr key={p.code_meti} className={`border-b ${i % 2 ? 'bg-slate-50' : 'bg-white'}`} data-testid={`top-row-${p.rank}`}>
                                                <td className="px-2 py-1 font-mono font-semibold text-slate-400">{p.rank}</td>
                                                <td className="px-2 py-1 font-medium">
                                                    {p.product_name}
                                                    {inPareto && i < 3 && (
                                                        <Badge className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 px-1 py-0">★</Badge>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1 text-slate-400 font-mono">{p.code_meti}</td>
                                                <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatCurrency(p.ca)}</td>
                                                <td className="px-2 py-1 text-right tabular-nums">{p.qty}</td>
                                                <td className="px-2 py-1 text-right tabular-nums">{p.share_pct.toFixed(2)}%</td>
                                                <td className="px-2 py-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="flex-1 bg-slate-200 rounded h-1.5 overflow-hidden min-w-[60px] max-w-[120px]">
                                                            <div
                                                                className={`h-full ${inPareto ? 'bg-emerald-500' : 'bg-slate-400'}`}
                                                                style={{ width: `${Math.min(100, p.cumulative_pct)}%` }}
                                                            />
                                                        </div>
                                                        <span className="tabular-nums text-[10px] text-slate-500 w-12 text-right">{p.cumulative_pct.toFixed(1)}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {!loading && products.length === 0 && (
                <Card><CardContent className="py-10 text-center text-slate-400">Aucune vente sur la période sélectionnée.</CardContent></Card>
            )}
        </div>
    );
}

function KpiCard({ icon, label, value, sub, accent = 'slate' }) {
    const colors = {
        blue: 'border-blue-200 bg-blue-50 text-blue-700',
        emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        amber: 'border-amber-200 bg-amber-50 text-amber-700',
        slate: 'border-slate-200 bg-white text-slate-700',
    };
    return (
        <div className={`border rounded-lg p-2 ${colors[accent]}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-80">
                {icon}{label}
            </div>
            <div className="text-base font-bold tabular-nums leading-tight mt-1">{value}</div>
            {sub && <div className="text-[10px] opacity-70">{sub}</div>}
        </div>
    );
}
